/**
 * FreeTextFieldsModal - Operatør-utfylling av frifelt (FreeText1-5) før utskrift.
 *
 * Vises når valgt rapport har en LabelDesigner-mal som inneholder ett eller flere
 * frifelt-plassholdere ({{FreeText1}}..{{FreeText5}}). Operatøren fyller inn verdiene
 * og ser en live PDF-forhåndsvisning av etiketten med verdiene plassert der designeren
 * la dem — før utskrift bekreftes.
 *
 * Bevisste UX-valg:
 * - Modalen lukkes IKKE ved klikk på bakteppet eller Escape — kun via Lukk/X.
 * - Modalen lukkes HELLER IKKE etter «Skriv ut»: resultatet vises i modalen, og
 *   operatøren kan justere teksten og skrive ut på nytt til de er fornøyd.
 *   (Feilskriving på fysiske etiketter er dyrt.)
 * - Tomme frifelt skrives ut som blankt.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Printer, Loader2, AlertCircle, PenLine, CheckCircle } from 'lucide-react';
import reportConfigurationService from '../../services/reportConfigurationService';
import type { LabelContext, LabelLayout } from '../../types/labelDesign';
import { useTranslation } from '../../contexts/I18nProvider';
import { PrinterSelect } from './PrinterSelect';

/** Gyldige frifelt-plassholdere — må matche FreeTextFieldKeys i backend PrintingService. */
export const FREE_TEXT_FIELD_KEYS = ['FreeText1', 'FreeText2', 'FreeText3', 'FreeText4', 'FreeText5'] as const;

/**
 * Finner hvilke frifelt (FreeText1-5) en LabelDesigner-layout bruker.
 * Returnerer unike nøkler i stigende rekkefølge, eller tom liste hvis
 * layouten mangler eller ikke bruker frifelt.
 */
export function extractFreeTextFields(layoutJson?: string | null): string[] {
  if (!layoutJson) return [];
  const regex = /\{\{\s*(FreeText[1-5])\s*\}\}/g;
  const found: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(layoutJson)) !== null) {
    if (found.indexOf(match[1]) === -1) found.push(match[1]);
  }
  return found.sort();
}

/** Resultat fra kallstedets utskrift — vises i modalen (som forblir åpen) */
export interface FreeTextPrintOutcome {
  success: boolean;
  message?: string;
}

interface FreeTextFieldsModalProps {
  isOpen: boolean;
  companyNo: string;
  /** LabelDesigner-layouten til valgt rapport (Reports.LayoutJson) — brukes til forhåndsvisning */
  layoutJson: string;
  /** Frifeltene malen bruker (fra extractFreeTextFields) */
  fields: string[];
  /** Rapportens beskrivelse — vises i tittelen */
  reportDescription?: string;
  /** Valgfri DB-kontekst (varenr, ordre, ...) så forhåndsvisningen viser reelle data */
  context?: LabelContext;
  onCancel: () => void;
  /**
   * Kjører utskriften med operatørens frifeltverdier og returnerer resultatet.
   * Modalen lukkes IKKE — resultatet vises, og operatøren kan justere og skrive ut igjen.
   * `printerIdOverride` er operatørens skrivervalg i modalen; null = automatisk
   * (hierarkisk oppløsning fra PackZone/Profil/Rapport).
   */
  onConfirm: (
    values: Record<string, string>,
    printerIdOverride: number | null
  ) => Promise<FreeTextPrintOutcome | void> | void;
}

export const FreeTextFieldsModal: React.FC<FreeTextFieldsModalProps> = ({
  isOpen,
  companyNo,
  layoutJson,
  fields,
  reportDescription,
  context,
  onCancel,
  onConfirm
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>({});
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [printOutcome, setPrintOutcome] = useState<FreeTextPrintOutcome | null>(null);
  // Skrivervalg (PrintNode-ID). null/tom = automatisk fra profil/rapport-hierarkiet
  const [printerId, setPrinterId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewUrlRef = useRef<string>('');

  const generatePreview = useCallback(async (currentValues: Record<string, string>) => {
    let layout: LabelLayout;
    try {
      layout = JSON.parse(layoutJson);
    } catch {
      setPreviewError(t('common.freeTextModal.previewError', {}, 'Kunne ikke lage forhåndsvisning'));
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const blob = await reportConfigurationService.getCustomLabelPreview(
        companyNo,
        layout,
        currentValues,
        context
      );
      const url = URL.createObjectURL(blob);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (err) {
      setPreviewError(
        err instanceof Error
          ? err.message
          : t('common.freeTextModal.previewError', {}, 'Kunne ikke lage forhåndsvisning')
      );
    } finally {
      setPreviewLoading(false);
    }
  }, [companyNo, layoutJson, context, t]);

  // Nullstill og lag første forhåndsvisning når modalen åpnes
  useEffect(() => {
    if (!isOpen) return;
    setValues({});
    setPreviewError(null);
    setPrintOutcome(null);
    setPrinting(false);
    setPrinterId(null);
    generatePreview({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Rydd opp blob-URL ved unmount
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleValueChange = (field: string, value: string) => {
    const next = { ...values, [field]: value };
    setValues(next);
    // Teksten er endret siden forrige utskrift — fjern gammelt utskriftsresultat
    setPrintOutcome(null);
    // Debounce: oppdater forhåndsvisningen når operatøren tar en skrivepause
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => generatePreview(next), 600);
  };

  const handlePrint = async () => {
    setPrinting(true);
    setPrintOutcome(null);
    try {
      const printerIdNum = printerId ? parseInt(printerId, 10) : null;
      const outcome = await onConfirm(values, printerIdNum);
      if (outcome) setPrintOutcome(outcome);
    } catch (err) {
      setPrintOutcome({
        success: false,
        message: err instanceof Error ? err.message : undefined
      });
    } finally {
      setPrinting(false);
    }
  };

  if (!isOpen) return null;

  const fieldLabel = (field: string) => {
    const n = field.replace('FreeText', '');
    return t('common.freeTextModal.fieldLabel', { '0': n }, `Frifelt ${n}`);
  };

  return (
    // Bakteppet lukker bevisst IKKE modalen — operatøren må ta et aktivt valg
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
              <PenLine className="text-blue-600" size={18} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('common.freeTextModal.title', {}, 'Fyll inn frifelt')}
              </h2>
              {reportDescription && (
                <p className="text-xs text-gray-500">{reportDescription}</p>
              )}
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label={t('common.buttons.close', {}, 'Lukk')}
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body: felter til venstre, forhåndsvisning til høyre */}
        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              {t(
                'common.freeTextModal.description',
                {},
                'Denne etiketten har frifelt. Fyll inn tekst og kontroller plasseringen i forhåndsvisningen før du skriver ut.'
              )}
            </p>
            {fields.map((field) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {fieldLabel(field)}
                </label>
                <input
                  type="text"
                  value={values[field] || ''}
                  onChange={(e) => handleValueChange(field, e.target.value)}
                  placeholder={t('common.freeTextModal.fieldPlaceholder', {}, 'Tekst som skal på etiketten...')}
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            ))}

            {/* Skrivervalg — tomt = automatisk oppløsning (PackZone → Profil → Rapport) */}
            <div className="pt-2 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.freeTextModal.printerLabel', {}, 'Skriver')}
              </label>
              <PrinterSelect
                value={printerId}
                onChange={setPrinterId}
                valueField="id"
                placeholder={t('common.freeTextModal.printerAuto', {}, 'Automatisk (fra profil/rapport)')}
                showTestButton={false}
                size="sm"
              />
            </div>
          </div>

          {/* Forhåndsvisning */}
          <div className="flex flex-col min-h-[260px]">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">
                {t('common.freeTextModal.previewTitle', {}, 'Forhåndsvisning')}
              </span>
              {previewLoading && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Loader2 size={12} className="animate-spin" />
                  {t('common.freeTextModal.previewLoading', {}, 'Oppdaterer...')}
                </span>
              )}
            </div>
            <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden bg-gray-100">
              {previewError ? (
                <div className="h-full flex items-center justify-center p-4">
                  <div className="flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                    <span>{previewError}</span>
                  </div>
                </div>
              ) : previewUrl ? (
                <iframe
                  src={`${previewUrl}#toolbar=0&navpanes=0`}
                  title={t('common.freeTextModal.previewTitle', {}, 'Forhåndsvisning')}
                  className="w-full h-full border-0"
                />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <Loader2 size={24} className="animate-spin" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer med utskriftsresultat — modalen forblir åpen så operatøren kan justere og skrive ut igjen */}
        <div className="flex items-center justify-between gap-2 p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex-1 min-w-0">
            {printOutcome && (
              printOutcome.success ? (
                <span className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle size={16} className="flex-shrink-0" />
                  {t('common.freeTextModal.printSuccess', {}, 'Etikett sendt til utskrift')}
                </span>
              ) : (
                <span className="flex items-start gap-2 text-sm text-red-700">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span className="truncate">
                    {t('common.freeTextModal.printFailed', {}, 'Utskriften feilet')}
                    {printOutcome.message ? `: ${printOutcome.message}` : ''}
                  </span>
                </span>
              )
            )}
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={printing}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {printOutcome?.success
              ? t('common.buttons.close', {}, 'Lukk')
              : t('common.buttons.cancel', {}, 'Avbryt')}
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={printing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {printing ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
            {t('common.buttons.print', {}, 'Skriv ut')}
          </button>
        </div>
      </div>
    </div>
  );
};
